import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import { 
  Users, Building2, Wrench, Briefcase, CreditCard, Eye, TrendingUp,
  LogOut, Trash2, ChevronRight, Calendar, DollarSign, AlertTriangle, CheckCircle
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminDashboard() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [services, setServices] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [subscriptionIssues, setSubscriptionIssues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, usersRes, jobsRes, servicesRes, transactionsRes, issuesRes] = await Promise.all([
        axios.get(`${API}/admin/stats`),
        axios.get(`${API}/admin/users`),
        axios.get(`${API}/admin/jobs`),
        axios.get(`${API}/admin/services`),
        axios.get(`${API}/admin/transactions`),
        axios.get(`${API}/admin/users/subscription-issues`).catch(() => ({ data: { issues: [] } }))
      ]);
      setStats(statsRes.data);
      setUsers(usersRes.data);
      setJobs(jobsRes.data);
      setServices(servicesRes.data);
      setTransactions(transactionsRes.data);
      setSubscriptionIssues(issuesRes.data.issues || []);
    } catch (error) {
      console.error("Failed to fetch admin data:", error);
      toast.error("Errore nel caricamento dati");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm("Eliminare questo utente?")) return;
    try {
      await axios.delete(`${API}/admin/users/${userId}`);
      toast.success("Utente eliminato");
      fetchData();
    } catch (error) {
      toast.error("Errore nell'eliminazione");
    }
  };

  const handleFixSubscription = async (userId, userName) => {
    if (!window.confirm(`Attivare l'abbonamento per ${userName}?`)) return;
    try {
      const expiresAt = new Date();
      expiresAt.setFullYear(expiresAt.getFullYear() + 1);
      await axios.put(`${API}/admin/user/${userId}/subscription`, {
        subscription_active: true,
        subscription_expires: expiresAt.toISOString()
      });
      toast.success(`Abbonamento attivato per ${userName}`);
      fetchData();
    } catch (error) {
      toast.error("Errore nell'attivazione");
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString("it-CH", { day: "2-digit", month: "2-digit", year: "numeric" });
  };

  const getUserTypeLabel = (type) => {
    switch (type) {
      case "candidate": return "Candidato";
      case "company": return "Azienda";
      case "service_provider": return "Fornitore";
      default: return type;
    }
  };

  const getUserTypeColor = (type) => {
    switch (type) {
      case "candidate": return "bg-red-100 text-red-700";
      case "company": return "bg-blue-100 text-blue-700";
      case "service_provider": return "bg-emerald-100 text-emerald-700";
      default: return "bg-slate-100 text-slate-700";
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100" data-testid="admin-dashboard">
      {/* Header */}
      <header className="bg-slate-900 text-white py-4 px-6 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <h1 className="text-xl font-bold">LavoroTicino Admin</h1>
          {stats?.launch_period?.is_active && (
            <Badge className="bg-[#DA291C]">Periodo lancio attivo</Badge>
          )}
        </div>
        <div className="flex items-center gap-4">
          <Link to="/" className="text-sm text-slate-400 hover:text-white">Vai al sito</Link>
          <Button variant="ghost" onClick={handleLogout} className="text-slate-400 hover:text-white">
            <LogOut className="w-4 h-4 mr-2" /> Esci
          </Button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Subscription Issues Alert */}
        {subscriptionIssues.length > 0 && (
          <div className="mb-6 bg-amber-50 border border-amber-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
              <div className="flex-1">
                <h3 className="font-semibold text-amber-800">Problemi Abbonamento ({subscriptionIssues.length})</h3>
                <p className="text-sm text-amber-700 mb-3">Utenti con annunci/servizi ma abbonamento non attivo</p>
                <div className="space-y-2">
                  {subscriptionIssues.map((issue) => (
                    <div key={issue.user_id} className="flex items-center justify-between bg-white p-2 rounded border">
                      <div>
                        <span className="font-medium text-slate-900">{issue.company_name || issue.name}</span>
                        <span className="text-xs text-slate-500 ml-2">({issue.email})</span>
                        <span className="text-xs text-slate-400 ml-2">• {issue.jobs_count} annunci, {issue.services_count} servizi</span>
                      </div>
                      <Button 
                        size="sm" 
                        onClick={() => handleFixSubscription(issue.user_id, issue.company_name || issue.name)}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs"
                      >
                        <CheckCircle className="w-3 h-3 mr-1" /> Attiva
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="overview">Panoramica</TabsTrigger>
            <TabsTrigger value="users">Utenti</TabsTrigger>
            <TabsTrigger value="content">Contenuti</TabsTrigger>
            <TabsTrigger value="payments">Pagamenti</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500">Utenti totali</p>
                      <p className="text-3xl font-bold text-slate-900">{stats?.users?.total || 0}</p>
                    </div>
                    <Users className="w-10 h-10 text-slate-300" />
                  </div>
                  <div className="mt-4 flex gap-2 text-xs">
                    <span className="text-red-600">{stats?.users?.candidates} candidati</span>
                    <span className="text-blue-600">{stats?.users?.companies} aziende</span>
                    <span className="text-emerald-600">{stats?.users?.service_providers} fornitori</span>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500">Abbonamenti attivi</p>
                      <p className="text-3xl font-bold text-emerald-600">{stats?.subscriptions?.total_active || 0}</p>
                    </div>
                    <CreditCard className="w-10 h-10 text-slate-300" />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500">Contenuti</p>
                      <p className="text-3xl font-bold text-slate-900">{(stats?.content?.jobs || 0) + (stats?.content?.services || 0)}</p>
                    </div>
                    <Briefcase className="w-10 h-10 text-slate-300" />
                  </div>
                  <div className="mt-4 flex gap-2 text-xs">
                    <span>{stats?.content?.jobs} lavori</span>
                    <span>{stats?.content?.services} servizi</span>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500">Ricavi totali</p>
                      <p className="text-3xl font-bold text-[#DA291C]">CHF {stats?.revenue?.total_chf || 0}</p>
                    </div>
                    <DollarSign className="w-10 h-10 text-slate-300" />
                  </div>
                  <p className="mt-4 text-xs text-slate-500">{stats?.revenue?.paid_transactions || 0} transazioni</p>
                </CardContent>
              </Card>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Eye className="w-5 h-5" /> Visualizzazioni
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <span className="text-slate-600">Annunci lavoro</span>
                      <span className="font-bold">{stats?.views?.jobs || 0}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-slate-600">Servizi</span>
                      <span className="font-bold">{stats?.views?.services || 0}</span>
                    </div>
                    <div className="flex justify-between items-center pt-2 border-t">
                      <span className="font-medium">Totale</span>
                      <span className="font-bold text-lg">{stats?.views?.total || 0}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Briefcase className="w-5 h-5" /> Interazioni
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <span className="text-slate-600">Candidature</span>
                      <span className="font-bold text-blue-600">{stats?.content?.applications || 0}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-slate-600">Messaggi inviati</span>
                      <span className="font-bold text-emerald-600">{stats?.content?.messages || 0}</span>
                    </div>
                    <div className="flex justify-between items-center pt-2 border-t">
                      <span className="font-medium">Engagement rate</span>
                      <span className="font-bold text-lg">
                        {stats?.views?.total > 0 
                          ? Math.round(((stats?.content?.applications || 0) / stats.views.total) * 100) 
                          : 0}%
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="w-5 h-5" /> Attività recente
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <span className="text-slate-600">Nuovi utenti (7 giorni)</span>
                      <span className="font-bold text-emerald-600">{stats?.recent?.new_users_7days || 0}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-slate-600">Candidature totali</span>
                      <span className="font-bold">{stats?.content?.applications || 0}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-slate-600">Richieste servizi</span>
                      <span className="font-bold">{stats?.content?.service_requests || 0}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users">
            <Card>
              <CardHeader>
                <CardTitle>Utenti registrati ({users.length})</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {users.map((user) => (
                    <div key={user.id} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{user.name}</span>
                          <Badge className={getUserTypeColor(user.user_type)}>{getUserTypeLabel(user.user_type)}</Badge>
                          {user.subscription_active && <Badge className="bg-emerald-100 text-emerald-700">Abbonato</Badge>}
                        </div>
                        <p className="text-sm text-slate-500">{user.email} • Registrato: {formatDate(user.created_at)}</p>
                      </div>
                      <Button variant="ghost" size="sm" className="text-red-600 hover:bg-red-50" onClick={() => handleDeleteUser(user.id)}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Content Tab */}
          <TabsContent value="content">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Briefcase className="w-5 h-5" /> Annunci lavoro ({jobs.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3 max-h-96 overflow-y-auto">
                    {jobs.map((job) => (
                      <div key={job.id} className="p-3 bg-slate-50 rounded-lg">
                        <div className="flex items-center justify-between">
                          <span className="font-medium">{job.title}</span>
                          <Badge variant={job.is_active ? "default" : "secondary"}>{job.is_active ? "Attivo" : "Inattivo"}</Badge>
                        </div>
                        <p className="text-sm text-slate-500">{job.company_name} • {job.location} • {job.views} views</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Wrench className="w-5 h-5" /> Servizi ({services.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3 max-h-96 overflow-y-auto">
                    {services.map((service) => (
                      <div key={service.id} className="p-3 bg-slate-50 rounded-lg">
                        <div className="flex items-center justify-between">
                          <span className="font-medium">{service.title}</span>
                          <Badge className="bg-emerald-100 text-emerald-700">{service.categoria}</Badge>
                        </div>
                        <p className="text-sm text-slate-500">{service.provider_name} • {service.location} • {service.views} views</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Payments Tab */}
          <TabsContent value="payments">
            <Card>
              <CardHeader>
                <CardTitle>Transazioni ({transactions.length})</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {transactions.map((tx) => (
                    <div key={tx.id} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                      <div>
                        <p className="font-medium">{tx.user_email}</p>
                        <p className="text-sm text-slate-500">{formatDate(tx.created_at)} • {tx.package_type}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold">CHF {tx.amount}</p>
                        <Badge className={tx.payment_status === "paid" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}>
                          {tx.payment_status === "paid" ? "Pagato" : tx.payment_status}
                        </Badge>
                      </div>
                    </div>
                  ))}
                  {transactions.length === 0 && (
                    <p className="text-center text-slate-500 py-8">Nessuna transazione</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
